import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import ViewLoader from '../../components/ViewLoader/ViewLoader';
import { CHINA_VERSION } from '../../globals';
import useAxios from '../../hooks/useAxios/useAxios';
import useDetermineSelectRoomSearchParameters from '../../hooks/useDetermineSelectRoomSearchParameters/useDetermineSelectRoomSearchParameters';
import useProductCode from '../../hooks/useProductCode/useProductCode';
import {
  setCurrentCurrency,
  setTimezone,
} from '../../redux/slices/appSettingsSlice/appSettingsSlice';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import { DiscoveryBelmondEmailNotValidModal } from './DiscoveryBelmondEmailNotValidModal';
import InvalidSpecialCodeModal from './InvalidSpecialCodeModal';
import { useValidateSelectRoomParams } from './hooks';

const SingleHotelViewWrapper = () => {
  const axios = useAxios();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { scriptsLoaded, metadataLoaded } = useOutletContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [timezoneLoaded, setTimezoneLoaded] = useState(false);
  const [invalidDiscoveryBelmondEmail, setInvalidDiscoveryBelmondEmail] =
    useState(false);

  const countryCode = useSelector((state) => state.appSettings.countryCode);
  const hotels = useSelector((state) => state.belmond.hotels);
  const currencySelectedByUser = useSelector(
    (state) => state.appSettings.currencies.selectedByUser
  );

  const productCode = useProductCode();
  const hotel = useSelector((state) => state.belmond.hotels[productCode]) || {};

  const { defaultValues, validateSpecialCode, validateRatePlanCode } =
    useDetermineSelectRoomSearchParameters(hotel);

  // perhaps move this somewhere else
  // so we dont need to run it for all views in the single hotel view wrapper
  const { allParamsValidated, parametersValidated } =
    useValidateSelectRoomParams(
      hotel,
      defaultValues,
      validateSpecialCode,
      validateRatePlanCode,
      axios,
      setInvalidDiscoveryBelmondEmail
    );

  // Set default currency if user hasn't selected one
  useEffect(() => {
    if (!currencySelectedByUser) {
      let currencyToUse = CHINA_VERSION ? 'CNY' : hotel?.currency || 'GBP';
      dispatch(setCurrentCurrency(currencyToUse));
    }
  }, [currencySelectedByUser, dispatch, hotel?.currency]);

  // Check for maintenance mode
  const checkMaintenance = useCallback(async () => {
    const { maintenance } = hotels[productCode] || {};
    if (!maintenance) return;

    if (pathname !== `/maintenance`) {
      navigate(`/maintenance?productCode=${productCode}`);
    }
  }, [hotels, navigate, pathname, productCode]);

  // checks when location changes
  useEffect(() => {
    checkMaintenance();
  }, [checkMaintenance]);

  // Redirect to home if no product code
  useEffect(() => {
    if (!productCode) {
      navigate('/');
    }
  }, [navigate, dispatch, hotel?.timezone, productCode]);

  useEffect(() => {
    if (['MAR', 'CSN'].includes(productCode) && countryCode === 'MX') {
      dispatch(setCurrentCurrency('MXN'));
    }
  }, [dispatch, hotel?.address, productCode, countryCode]);

  useEffect(() => {
    if (!timezoneLoaded && hotel?.timezone) {
      dispatch(setTimezone(hotel.timezone));
      setTimezoneLoaded(true);
    }
  }, [dispatch, hotel.timezone, timezoneLoaded]);

  const outletReady =
    scriptsLoaded && metadataLoaded && allParamsValidated && timezoneLoaded;

  const handleToggleInvalidEmailModal = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('specialCodeType');
    params.delete('discovery');
    params.delete('specialCodeValue');
    setSearchParams(params);
    setInvalidDiscoveryBelmondEmail(false);
    window.location.reload();
  };

  return (
    <div data-testid="single-hotel-view">
      <DiscoveryBelmondEmailNotValidModal
        isOpen={invalidDiscoveryBelmondEmail}
        onToggle={handleToggleInvalidEmailModal}
      />
      <InvalidSpecialCodeModal
        validationResult={parametersValidated.specialCode}
      />
      <Header
        phone={hotel?.phone}
        hotelCurrency={hotel?.currency}
        productCode={productCode}
      />

      {outletReady && <Outlet context={{ hotel, productCode: productCode }} />}

      {!outletReady && <ViewLoader />}

      <Footer
        loading={!outletReady}
        instagramLink={hotel?.metadata?.instagramLink}
        facebookLink={hotel?.metadata?.facebookLink}
        address={`${hotel?.address?.address}, ${hotel?.address?.city}, ${hotel?.address?.postalCode} `}
        phone={hotel?.phone}
        productCode={productCode}
        contactUsLink={hotel?.metadata?.contactUsLink}
        findUsLink={hotel?.metadata?.findUsLink}
      />
    </div>
  );
};

export default SingleHotelViewWrapper;
